import {
  type NotificationRenderContext,
  notificationTypeDefinition,
  renderNotification,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Field, FieldContent, FieldDescription, FieldLabel } from "@loot-os/ui/components/field";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@loot-os/ui/components/sheet";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useSessionQuery } from "../lib/authQueries";
import {
  draftFromContent,
  draftToTemplate,
  hasNoIssues,
  isDefaultOverride,
  type NotificationTemplateRow,
  notificationDraftIssues,
  sameDraft,
  templateOverride,
  unavailableNote,
} from "../lib/notifications";
import { useTRPC } from "../lib/trpc";
import { useNotificationTemplateWrites } from "../lib/useNotifications";
import { NotificationContentFields } from "./NotificationContentFields";
import { NotificationPreview } from "./NotificationPreview";

/** The editor of one system type, in a sheet: its content, a live preview, save, reset and test. */
export function NotificationTemplateSheet({
  row,
  open,
  onOpenChange,
  context,
  theme,
  uploadsAvailable,
  canEdit,
}: {
  /** The type being edited; kept while the sheet closes so its content stays for the exit. */
  row: NotificationTemplateRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: NotificationRenderContext;
  theme?: StorefrontTheme;
  uploadsAvailable: boolean;
  canEdit: boolean;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="gap-0 overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-4xl">
        {row && (
          <TemplateEditor
            key={row.type}
            row={row}
            context={context}
            theme={theme}
            uploadsAvailable={uploadsAvailable}
            canEdit={canEdit}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function TemplateEditor({
  row,
  context,
  theme,
  uploadsAvailable,
  canEdit,
}: {
  row: NotificationTemplateRow;
  context: NotificationRenderContext;
  theme?: StorefrontTheme;
  uploadsAvailable: boolean;
  canEdit: boolean;
}) {
  const trpc = useTRPC();
  const session = useSessionQuery();
  const [now] = useState(() => new Date());
  const [draft, setDraft] = useState(() => draftFromContent(row.template));
  const [enabled, setEnabled] = useState(row.template.enabled);
  const [attempted, setAttempted] = useState(false);
  const { update, reset } = useNotificationTemplateWrites();

  const resetToDefault = () =>
    reset.mutate(
      { type: row.type },
      {
        onSuccess: (template) => {
          setDraft(draftFromContent(template));
          setEnabled(template.enabled);
          toast.success(`${row.label} is back to its default.`);
        },
      },
    );
  const test = useMutation(
    trpc.notifications.sendTemplateTest.mutationOptions({
      onSuccess: () => toast.success("Test sent. Open the storefront's bell to see it."),
    }),
  );

  const variables = row.variables.map((variable) => variable.name);
  const issues = notificationDraftIssues(draft, variables);
  const dirty =
    enabled !== row.template.enabled || !sameDraft(draft, draftFromContent(row.template));
  const busy = update.isPending || reset.isPending;
  const definition = notificationTypeDefinition(row.type);
  const rendered = renderNotification(
    {
      kind: "system",
      type: row.type,
      data: definition.sample,
      template: draftToTemplate(draft, enabled),
    },
    context,
  );

  const save = () => {
    setAttempted(true);
    if (!hasNoIssues(issues)) return;
    const override = templateOverride(draft, enabled, row.defaults);
    // An edit back to every default is a reset, so the type reads as not customized.
    if (isDefaultOverride(override)) resetToDefault();
    else {
      update.mutate(
        { type: row.type, override },
        { onSuccess: () => toast.success(`${row.label} saved.`) },
      );
    }
  };

  return (
    <form
      className="flex min-h-full flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <SheetHeader className="border-b pr-12">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle className="text-base">{row.label}</SheetTitle>
          {row.customized && <Badge variant="secondary">Customized</Badge>}
          {!row.available && <Badge variant="outline">Not sent</Badge>}
        </div>
        <SheetDescription>
          {row.description}
          {!row.available && ` ${unavailableNote(row, context.terms)}`}
        </SheetDescription>
      </SheetHeader>

      <div className="grid flex-1 gap-8 p-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="grid content-start gap-6">
          <Field orientation="horizontal">
            <Switch
              id="template-enabled"
              checked={enabled}
              disabled={!canEdit || busy}
              onCheckedChange={setEnabled}
            />
            <FieldContent>
              <FieldLabel htmlFor="template-enabled">Send this notification</FieldLabel>
              <FieldDescription>
                Off, shoppers are never sent it in this environment.
              </FieldDescription>
            </FieldContent>
          </Field>
          <NotificationContentFields
            id="template"
            draft={draft}
            setDraft={setDraft}
            issues={issues}
            showAllIssues={attempted}
            variables={row.variables}
            context={context}
            category={row.category}
            disabled={!canEdit || busy}
            uploadsAvailable={uploadsAvailable}
          />
        </div>
        <div className="grid content-start gap-2 lg:sticky lg:top-4 lg:self-start">
          <p className="text-sm font-medium">Preview</p>
          <NotificationPreview
            category={row.category}
            rendered={rendered}
            theme={theme}
            now={now}
          />
          <p className="text-xs text-muted-foreground">
            With sample details, in this store's words and currency.
            {!enabled && " Turned off, so shoppers won't get it."}
          </p>
        </div>
      </div>

      {canEdit && (
        <SheetFooter className="sticky bottom-0 flex-row flex-wrap items-center gap-2 border-t bg-popover">
          <Button type="submit" disabled={!dirty || busy}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          {row.customized && (
            <Button type="button" variant="outline" disabled={busy} onClick={resetToDefault}>
              Reset to default
            </Button>
          )}
          <p className="text-xs text-muted-foreground sm:ml-auto">
            {dirty
              ? "Save first: a test sends the saved version."
              : `A test goes to ${session.data?.user.email ?? "you"} on this storefront.`}
          </p>
          <Button
            type="button"
            variant="outline"
            disabled={dirty || test.isPending}
            onClick={() => test.mutate({ type: row.type })}
          >
            {test.isPending ? "Sending…" : "Send test"}
          </Button>
        </SheetFooter>
      )}
    </form>
  );
}
